/**
 * Is an Inbox item still true? Blocked-environment and failed-landing cards are re-evaluated on
 * load against the newest decrypted machine / landing reports: a condition that no longer holds
 * resolves itself; an item we cannot re-check says why ("Waiting for a fresh report from Mac")
 * and an old one says it may be outdated instead of asserting stale state.
 */
import { SCHEDULE_OPAQUE_TITLE } from './stall-text.js'
import type { InboxItem, Landing, Machine, MachineBlocker, RunSummary, Task } from './types.js'

export { stallReason } from './stall-text.js'

/** A machine report older than this cannot vouch for "still blocked". */
export const REPORT_STALE_MS = 30 * 60_000
/** A failed landing nobody retried for this long is flagged as possibly outdated. */
export const LANDING_STALE_MS = 6 * 3_600_000

export interface Freshness {
  state: 'current' | 'resolved' | 'waiting' | 'outdated'
  /** Plain-language status line for the detail pane (and the row). */
  text: string
  /** When the evidence was last checked (report time). */
  checkedAt?: number
  blocker?: MachineBlocker
  landing?: Landing
  machine?: Pick<Machine, 'id' | 'name' | 'online' | 'last_seen_at'>
}

export const isBlockerItem = (i: Pick<InboxItem, 'kind' | 'category'>) =>
  i.kind === 'blocked' && (i.category ?? '').startsWith('stall:')
export const isLandingFailure = (i: Pick<InboxItem, 'category'>) => i.category === 'landing-failed'

export function ago(ms: number): string {
  const m = Math.max(0, Math.round(ms / 60_000))
  if (m < 1) return 'just now'
  if (m < 60) return `${m} min ago`
  const h = Math.round(m / 60)
  if (h < 48) return `${h} h ago`
  return `${Math.round(h / 24)} d ago`
}

type BlockerBody = Partial<MachineBlocker> & { machineId?: string }

export function blockerFreshness(
  item: InboxItem,
  machines: readonly Machine[],
  now: number,
  /** This browser cannot read encrypted reports (default: it can). */
  locked = false,
): Freshness {
  const body = (item.body ?? {}) as BlockerBody
  const machine = machines.find((m) => m.id === (body.machineId ?? item.machineId))
  const name = machine?.name ?? 'the machine'
  const since = body.detectedAt ?? item.createdAt
  const opaque = body.title === SCHEDULE_OPAQUE_TITLE
  const m = machine ? { machine } : {}
  if (machine?.reportAt !== undefined && machine.blockers !== undefined) {
    const match = machine.blockers.find(
      (b) =>
        b.id === body.id ||
        (b.runId === body.runId && (b.detectedAt === body.detectedAt || b.cause === body.cause)),
    )
    if (!match) {
      if (machine.reportAt >= since)
        return {
          state: 'resolved',
          text: `${name} no longer reports this (its report ${ago(now - machine.reportAt)}).`,
          checkedAt: machine.reportAt,
          ...m,
        }
      return {
        state: 'waiting',
        text: `Waiting for a fresh report from ${name}: its last report predates this.`,
        checkedAt: machine.reportAt,
        ...m,
      }
    }
    const age = now - machine.reportAt
    return age > REPORT_STALE_MS
      ? {
          state: 'outdated',
          text: `May be outdated: ${name} last reported ${ago(age)}${machine.online ? '' : ' and is offline'}. Re-check to confirm.`,
          checkedAt: machine.reportAt,
          blocker: match,
          ...m,
        }
      : {
          state: 'current',
          text: `${name} still reports this (checked ${ago(age)}).`,
          checkedAt: machine.reportAt,
          blocker: match,
          ...m,
        }
  }
  if (machine && !machine.online)
    return {
      state: 'waiting',
      text: `Waiting for a fresh report from ${name}: it has been offline${machine.last_seen_at ? ` since ${ago(now - machine.last_seen_at)}` : ''}.`,
      ...m,
    }
  if (opaque)
    return {
      state: 'waiting',
      // "Unlock this browser" only when the browser really is locked.
      text: locked
        ? `Waiting for a fresh report from ${name}. Unlock this browser or re-check the machine.`
        : `No report from ${name} since ${new Date(since).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}; re-check the machine.`,
      ...m,
    }
  const detail = body.title ? (body as MachineBlocker) : undefined
  return now - since > REPORT_STALE_MS
    ? {
        state: 'outdated',
        text: `May be outdated: detected ${ago(now - since)}. Re-check ${name} to confirm.`,
        ...(detail ? { blocker: detail } : {}),
        ...m,
      }
    : {
        state: 'current',
        text: `Detected ${ago(now - since)} on ${name}.`,
        ...(detail ? { blocker: detail } : {}),
        ...m,
      }
}

export function landingFreshness(
  item: InboxItem,
  landings: readonly Landing[],
  machines: readonly Machine[],
  now: number,
): Freshness {
  const body = (item.body ?? {}) as { landingId?: string }
  const landing = landings.find((l) => l.id === body.landingId)
  const machine = machines.find((x) => x.id === (landing?.machineId ?? item.machineId))
  const name = machine?.name ?? 'the machine'
  const m = machine ? { machine } : {}
  if (!landing)
    return {
      state: 'waiting',
      text: `Waiting for a fresh report from ${name}: this landing's record has not arrived on this device.`,
      ...m,
    }
  const newer = landings
    .filter(
      (o) =>
        o.id !== landing.id &&
        o.updatedAt > landing.updatedAt &&
        ((landing.workstreamId && o.workstreamId === landing.workstreamId) ||
          (landing.branch && o.branch === landing.branch)) &&
        ['landed', 'running', 'queued'].includes(o.state),
    )
    .sort((a, b) => b.updatedAt - a.updatedAt)[0]
  if (newer)
    return {
      state: 'resolved',
      text: `A newer landing of ${newer.branch ?? 'this branch'} ${newer.state === 'landed' ? 'landed' : `is ${newer.state}`}.`,
      checkedAt: newer.updatedAt,
      landing,
      ...m,
    }
  if (landing.state !== 'failed')
    return {
      state: 'resolved',
      text: `This landing is now ${landing.state.replace('_', ' ')}.`,
      checkedAt: landing.updatedAt,
      landing,
      ...m,
    }
  const age = now - landing.updatedAt
  return age > LANDING_STALE_MS
    ? {
        state: 'outdated',
        text: `May be outdated: it failed ${ago(age)} and nothing has retried it since. Retry, or open the run to check its branch.`,
        checkedAt: landing.updatedAt,
        landing,
        ...m,
      }
    : {
        state: 'current',
        text: `Failed ${ago(age)} on ${name}.`,
        checkedAt: landing.updatedAt,
        landing,
        ...m,
      }
}

const TERMINAL_RUN = new Set(['done', 'stopped', 'failed', 'cancelled', 'error'])
const LIVE_RUN = new Set(['running', 'waiting', 'compacting'])
/** Stall causes that a run producing output afterwards disproves. */
const ACTIVITY_CAUSES = new Set(['no_output', 'no-output', 'looping'])

/**
 * A blocker is moot once its run has ended, its task landed, the run moved on after the stall
 * was detected (for "no output" style stalls) or a newer run of the same task is live.
 */
export function blockerRunResolution(
  item: InboxItem,
  runs: readonly Pick<
    RunSummary,
    'runId' | 'status' | 'updatedAt' | 'startedAt' | 'taskId' | 'landing' | 'typingElsewhere'
  >[],
  tasks: readonly Pick<Task, 'id' | 'status'>[],
): string | undefined {
  const body = (item.body ?? {}) as BlockerBody
  const runId = body.runId ?? item.runId
  const run = runs.find((r) => r.runId === runId)
  if (!run) return undefined
  const detectedAt = body.detectedAt ?? item.createdAt
  const status = String(run.status)
  if (TERMINAL_RUN.has(status)) return `The run has ended (${status}).`
  if (run.landing?.state === 'landed') return 'The run landed.'
  if (run.taskId && tasks.some((t) => t.id === run.taskId && t.status === 'done'))
    return 'Its task is done.'
  const cause = (body.cause ?? item.category?.replace(/^stall:/, '') ?? '').toString()
  if (ACTIVITY_CAUSES.has(cause) && run.updatedAt !== undefined && run.updatedAt > detectedAt)
    return 'The run produced output after this was detected.'
  if (
    run.taskId &&
    runs.some(
      (o) =>
        o.runId !== run.runId &&
        o.taskId === run.taskId &&
        (LIVE_RUN.has(String(o.status)) || o.typingElsewhere) &&
        (o.startedAt ?? o.updatedAt ?? 0) > (run.startedAt ?? 0),
    )
  )
    return 'A newer run of the same task is live.'
  return undefined
}

export function itemFreshness(
  item: InboxItem,
  ctx: {
    machines: readonly Machine[]
    landings: readonly Landing[]
    now: number
    runs?: readonly RunSummary[]
    tasks?: readonly Pick<Task, 'id' | 'status'>[]
    locked?: boolean
  },
): Freshness | undefined {
  if (isBlockerItem(item)) {
    const why = blockerRunResolution(item, ctx.runs ?? [], ctx.tasks ?? [])
    if (why) return { state: 'resolved', text: why }
    return blockerFreshness(item, ctx.machines, ctx.now, ctx.locked)
  }
  if (isLandingFailure(item)) return landingFreshness(item, ctx.landings, ctx.machines, ctx.now)
  return undefined
}
